/*****************************************************************
 *  CUSTOMER BUTLER - THE OTHER RELATED LISTS                      *
 *  File: apps/web/src/pages/customer/RelatedLists.js              *
 *  What it does: the related-list tabs that are rows of Butler    *
 *  records:                                                       *
 *    PropertiesTab   a customer's properties (+ Add property)     *
 *    ActivitiesTab   logged calls, emails, visits (+ Log one),    *
 *                    then what the other Butlers did (2026-10-14, *
 *                    SuiteTimeline.js)                            *
 *    ContactsTab     a customer's extra people (+ Add contact)    *
 *    PeopleTab       the people on a property                     *
 *    ConsentTab      provable opt-ins (+ Record consent)          *
 *  and the one small-form sheet every "Add" button opens.         *
 *  The sheet, the button row and the box lists are exported       *
 *  (2026-10-15) so the Zoho-style record blocks use the very same *
 *  forms and doors (one copy of each).                            *
 *  Reads/Writes: Customer Butler's own doors (customerApi.js).    *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE SMALL FORM SHEET                               *
 *  SECTION 2 - PROPERTIES                                         *
 *  SECTION 3 - ACTIVITIES                                         *
 *  SECTION 4 - CONTACTS AND PEOPLE                                *
 *  SECTION 5 - CONSENT                                            *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './customerIcons';
import { QuietLine, Sheet, StatusChip } from './customerParts';
import { formatMoment } from './customerDates';
import { useCustomerAccessView } from './customerAccessView';
import { SuiteTimelinePanel } from './SuiteTimeline';
import {
  addProperty, fetchTimeline, logActivity, fetchPropertyHistory,
  addContact, fetchConsent, recordConsent,
} from './customerApi';

/*****************************************************************
 *  SECTION 1 - THE SMALL FORM SHEET                               *
 *  boxes: [{ key, label, type: 'text'|'email'|'tel'|'textarea'|   *
 *           'choice', choices: [{ value, label }] }]              *
 *****************************************************************/

/* ---- SmallFormBox({ box, value, onType }) -- one labelled box ---- */
function SmallFormBox({ box, value, onType }) {
  const boxId = `cb-form-${box.key}`;

  let boxInput = null;

  if (box.type === 'textarea') {
    boxInput = <textarea id={boxId} className="cb-textarea" value={value} onChange={(changeEvent) => onType(changeEvent.target.value)} />;
  } else if (box.type === 'choice') {
    boxInput = (
      <select id={boxId} className="cb-select" value={value} onChange={(changeEvent) => onType(changeEvent.target.value)}>
        {box.choices.map((oneChoice) => <option key={oneChoice.value} value={oneChoice.value}>{oneChoice.label}</option>)}
      </select>
    );
  } else {
    boxInput = <input id={boxId} className="cb-input" type={box.type || 'text'} value={value} onChange={(changeEvent) => onType(changeEvent.target.value)} />;
  }

  return (
    <div>
      <label className="cb-label" htmlFor={boxId}>{box.label}</label>
      {boxInput}
    </div>
  );
}

/* ---- SmallFormSheet({ title, boxes, startValues, saveLabel,
                         onSave, onClose }) ----------------------
   onSave(values) returns the door's { ok, error }; the sheet
   shows a refusal in words and stays open.                      */
export function SmallFormSheet({ title, boxes, startValues, saveLabel, onSave, onClose }) {
  const [typedValues, setTypedValues] = useState(startValues || {});
  const [isSaving, setIsSaving] = useState(false);
  const [problemWords, setProblemWords] = useState('');

  async function saveForm(submitEvent) {
    submitEvent.preventDefault();
    setIsSaving(true);
    setProblemWords('');

    const saveAnswer = await onSave(typedValues);

    if (!saveAnswer.ok) {
      setIsSaving(false);
      setProblemWords((saveAnswer.error && saveAnswer.error.message) || 'That did not save. Nothing was added.');
    }
  }

  return (
    <Sheet title={title} onClose={onClose}>
      <form onSubmit={saveForm}>
        {boxes.map((oneBox) => (
          <SmallFormBox
            key={oneBox.key}
            box={oneBox}
            value={typedValues[oneBox.key] || ''}
            onType={(typedValue) => setTypedValues((valuesNow) => ({ ...valuesNow, [oneBox.key]: typedValue }))}
          />
        ))}
        {problemWords ? <p className="cb-edit-error" role="alert">{problemWords}</p> : null}
        <div className="cb-sheet-acts">
          <button type="button" className="bc-btn is-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="bc-btn is-primary" disabled={isSaving}>{isSaving ? 'Saving' : saveLabel}</button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---- AddButtonRow({ label, onAdd }) -- the one button above a list ----
   Every "Add property / Add contact / Log activity / Record consent"
   button is this one, so the level rule is written once here.
   WHY (2026-10-14, Honest UI, James via Fable): Look up adds nothing,
   so it is never offered the button the server would refuse.       */
export function AddButtonRow({ label, onAdd }) {
  const accessView = useCustomerAccessView(); // what this person may be shown

  if (!accessView.mayWrite) {
    return null;
  }

  return (
    <div className="cb-panel-acts">
      <button type="button" className="bc-btn is-quiet" onClick={onAdd}><Icon name="plus" /> {label}</button>
    </div>
  );
}

/* ---- WHY_BOX -- the one optional "Why" line on an Add sheet ----
   WHY (2026-10-14, James; BACKLOG 312): every change is recorded
   with who, when and why. Adding a property or a contact has room
   for one short optional line; it goes to the server as `why`.
   Left empty, the server writes a default that names the screen. */
const WHY_BOX = { key: 'why', label: 'Why (optional)', type: 'text' };

/*****************************************************************
 *  SECTION 2 - PROPERTIES                                         *
 *****************************************************************/

export const NEW_PROPERTY_BOXES = [
  { key: 'label', label: 'Property Name', type: 'text' },
  { key: 'addressLine1', label: 'Street', type: 'text' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'state', label: 'State', type: 'text' },
  { key: 'zip', label: 'Zip code', type: 'text' },
  WHY_BOX,
];

/* ---- streetWords(property) -- "12 Oak St, Weaverville, NC" ---- */
export function streetWords(property) {
  const streetParts = [property.addressLine1, property.city, property.state].filter(Boolean);

  return streetParts.join(', ');
}

/* ---- PropertiesTab({ customerId, properties, onChanged, addRequest }) --
   addRequest (optional, 2026-10-15): a number the HR look's "Add a
   property" button raises; each new number opens the Add sheet, so
   the button does exactly what it says (Honest UI).             */
export function PropertiesTab({ customerId, properties, onChanged, addRequest }) {
  const [isAdding, setIsAdding] = useState(false);
  const accessView = useCustomerAccessView(); // the sheet opens only for a level that may add

  useEffect(() => {
    const wasAskedToAdd = Number(addRequest) > 0;

    if (wasAskedToAdd && accessView.mayWrite) {
      setIsAdding(true);
    }
  }, [addRequest, accessView.mayWrite]);

  async function saveNewProperty(typedValues) {
    const addAnswer = await addProperty({ ...typedValues, customerId, customFields: {} });

    if (addAnswer.ok) {
      setIsAdding(false);
      onChanged();
    }

    return addAnswer;
  }

  return (
    <>
      <AddButtonRow label="Add property" onAdd={() => setIsAdding(true)} />
      <div className="cb-panel">
        {properties.length === 0 ? <QuietLine icon="home">No properties yet.</QuietLine> : null}
        <ul className="cb-list-rows">
          {properties.map((oneProperty) => {
            const nameWords = oneProperty.label || streetWords(oneProperty) || 'Unnamed property';
            const placeWords = [streetWords(oneProperty), oneProperty.zip].filter(Boolean).join(' ');
            const showPlace = placeWords && placeWords !== nameWords;

            return (
              <li key={oneProperty.id} className="cb-item">
                <Link className="cb-item-name" to={`/properties/${oneProperty.id}`}>{nameWords}</Link>
                {showPlace ? <div className="cb-item-meta">{placeWords}</div> : null}
              </li>
            );
          })}
        </ul>
      </div>
      {isAdding ? (
        <SmallFormSheet title="Add property" boxes={NEW_PROPERTY_BOXES} saveLabel="Add property" onSave={saveNewProperty} onClose={() => setIsAdding(false)} />
      ) : null}
    </>
  );
}

/*****************************************************************
 *  SECTION 3 - ACTIVITIES                                         *
 *  Zoho's "Open Activities" and "Closed Activities" are Butler's  *
 *  timeline of logged touches. Under them (2026-10-14): what the  *
 *  other Butlers did for this customer or property.               *
 *****************************************************************/

export const ACTIVITY_BOXES = [
  {
    key: 'channel',
    label: 'What kind',
    type: 'choice',
    choices: [
      { value: 'call', label: 'Call' },
      { value: 'email', label: 'Email' },
      { value: 'sms', label: 'Text message' },
      { value: 'meeting', label: 'Visit or meeting' },
      { value: 'note', label: 'Note' },
    ],
  },
  {
    key: 'direction',
    label: 'Which way',
    type: 'choice',
    choices: [
      { value: 'outbound', label: 'We reached out' },
      { value: 'inbound', label: 'They reached us' },
    ],
  },
  { key: 'subject', label: 'Subject', type: 'text' },
  { key: 'body', label: 'What was said', type: 'textarea' },
];

// The plain words for each stored channel word.
export const CHANNEL_WORDS = { call: 'Call', email: 'Email', sms: 'Text message', meeting: 'Visit or meeting', note: 'Note', system: 'Butler' };

/* ---- ActivityRows({ items }) -- the rows, newest first ---- */
function ActivityRows({ items }) {
  if (items.length === 0) {
    return <QuietLine icon="clock">No activity yet.</QuietLine>;
  }

  return (
    <ul className="cb-list-rows">
      {items.map((oneItem) => (
        <li key={oneItem.id} className="cb-item">
          <div className="cb-item-top">
            <span className="cb-item-name">{oneItem.subject || CHANNEL_WORDS[oneItem.channel] || 'Activity'}</span>
            <span className="cb-item-meta">{formatMoment(oneItem.occurredAt)}</span>
          </div>
          <div className="cb-item-meta">{CHANNEL_WORDS[oneItem.channel] || oneItem.channel}</div>
          {oneItem.body ? <div className="cb-item-body">{oneItem.body}</div> : null}
        </li>
      ))}
    </ul>
  );
}

/* ---- ActivitiesTab({ customerId, propertyId }) -----------------
   A customer's timeline (with "Log activity"), or a property's
   service history when propertyId is given.                     */
export function ActivitiesTab({ customerId, propertyId }) {
  const [items, setItems] = useState(null);
  const [isLogging, setIsLogging] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      const loadedItems = propertyId ? await fetchPropertyHistory(propertyId) : await fetchTimeline(customerId);

      setItems(Array.isArray(loadedItems) ? loadedItems : []);
    } catch (loadError) {
      setItems([]);
    }
  }, [customerId, propertyId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  async function saveActivity(typedValues) {
    const logAnswer = await logActivity(customerId, typedValues);

    if (logAnswer.ok) {
      setIsLogging(false);
      loadItems();
    }

    return logAnswer;
  }

  const canLog = Boolean(customerId) && !propertyId;

  return (
    <>
      {canLog ? <AddButtonRow label="Log activity" onAdd={() => setIsLogging(true)} /> : null}
      <div className="cb-panel">
        {items === null ? <QuietLine icon="clock">Loading activity</QuietLine> : <ActivityRows items={items} />}
      </div>
      {isLogging ? (
        <SmallFormSheet
          title="Log activity"
          boxes={ACTIVITY_BOXES}
          startValues={{ channel: 'call', direction: 'outbound' }}
          saveLabel="Log it"
          onSave={saveActivity}
          onClose={() => setIsLogging(false)}
        />
      ) : null}
      {/* WHY (2026-10-14, James via Fable; readiness 16): the Activities tab
          shows what the suite did for this customer, not only logged calls. */}
      <SuiteTimelinePanel customerId={customerId} propertyId={propertyId} />
    </>
  );
}

/*****************************************************************
 *  SECTION 4 - CONTACTS AND PEOPLE                                *
 *****************************************************************/

export const NEW_CONTACT_BOXES = [
  { key: 'firstName', label: 'First Name', type: 'text' },
  { key: 'lastName', label: 'Last Name', type: 'text' },
  { key: 'role', label: 'Role (owner, tenant, spouse ...)', type: 'text' },
  { key: 'phone', label: 'Phone', type: 'tel' },
  { key: 'email', label: 'Email', type: 'email' },
  WHY_BOX,
];

/* ---- ContactsTab({ customerId, contacts, onChanged }) ---------- */
export function ContactsTab({ customerId, contacts, onChanged }) {
  const [isAdding, setIsAdding] = useState(false);

  async function saveNewContact(typedValues) {
    const addAnswer = await addContact({ ...typedValues, customerId });

    if (addAnswer.ok) {
      setIsAdding(false);
      onChanged();
    }

    return addAnswer;
  }

  return (
    <>
      <AddButtonRow label="Add contact" onAdd={() => setIsAdding(true)} />
      <div className="cb-panel">
        {contacts.length === 0 ? <QuietLine icon="people">No extra contacts yet.</QuietLine> : null}
        <ul className="cb-list-rows">
          {contacts.map((oneContact) => {
            const nameWords = [oneContact.firstName, oneContact.lastName].filter(Boolean).join(' ') || 'No name';
            const reachWords = [oneContact.role, oneContact.phone, oneContact.email].filter(Boolean).join(', ');

            return (
              <li key={oneContact.id} className="cb-item">
                <div className="cb-item-top">
                  <span className="cb-item-name">{nameWords}</span>
                  {oneContact.isPrimary ? <StatusChip tone="brand" icon="check" word="Main contact" /> : null}
                </div>
                {reachWords ? <div className="cb-item-meta">{reachWords}</div> : null}
              </li>
            );
          })}
        </ul>
      </div>
      {isAdding ? (
        <SmallFormSheet title="Add contact" boxes={NEW_CONTACT_BOXES} saveLabel="Add contact" onSave={saveNewContact} onClose={() => setIsAdding(false)} />
      ) : null}
    </>
  );
}

/* ---- PeopleTab({ owner, linkedPeople }) ------------------------
   The people on a property: its owner first, then the others it
   is linked to. Each opens their customer record.               */
export function PeopleTab({ owner, linkedPeople }) {
  const everyone = [];

  if (owner) {
    everyone.push({ ...owner, roleWords: 'Owner' });
  }

  for (const onePerson of linkedPeople || []) {
    const isTheOwner = owner && onePerson.id === owner.id;

    if (!isTheOwner) {
      everyone.push({ ...onePerson, roleWords: 'Linked' });
    }
  }

  return (
    <div className="cb-panel">
      {everyone.length === 0 ? <QuietLine icon="people">No people on this property yet.</QuietLine> : null}
      <ul className="cb-list-rows">
        {everyone.map((onePerson) => {
          const reachWords = [onePerson.roleWords, onePerson.primaryPhone, onePerson.primaryEmail].filter(Boolean).join(', ');

          return (
            <li key={onePerson.id} className="cb-item">
              <Link className="cb-item-name" to={`/customers/${onePerson.id}`}>{onePerson.displayName || 'Unnamed customer'}</Link>
              <div className="cb-item-meta">{reachWords}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/*****************************************************************
 *  SECTION 5 - CONSENT                                            *
 *****************************************************************/

export const CONSENT_BOXES = [
  {
    key: 'consentType',
    label: 'Consent to',
    type: 'choice',
    choices: [
      { value: 'marketing_email', label: 'Marketing email' },
      { value: 'marketing_sms', label: 'Marketing text messages' },
      { value: 'call', label: 'Phone calls' },
      { value: 'data_processing', label: 'Keeping their data' },
    ],
  },
  {
    key: 'status',
    label: 'Answer',
    type: 'choice',
    choices: [
      { value: 'granted', label: 'Said yes' },
      { value: 'withdrawn', label: 'Took it back' },
    ],
  },
  { key: 'basis', label: 'How they said it (for example: ticked the box)', type: 'text' },
  { key: 'source', label: 'Where (web form, phone call ...)', type: 'text' },
];

// Plain words for the stored consent words.
export const CONSENT_WORDS = { marketing_email: 'Marketing email', marketing_sms: 'Marketing text messages', call: 'Phone calls', data_processing: 'Keeping their data' };

/* ---- ConsentTab({ customerId }) -------------------------------- */
export function ConsentTab({ customerId }) {
  const [records, setRecords] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  const loadRecords = useCallback(async () => {
    try {
      setRecords(await fetchConsent(customerId));
    } catch (loadError) {
      setRecords([]);
    }
  }, [customerId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  async function saveConsent(typedValues) {
    const saveAnswer = await recordConsent(customerId, typedValues);

    if (saveAnswer.ok) {
      setIsAdding(false);
      loadRecords();
    }

    return saveAnswer;
  }

  return (
    <>
      <AddButtonRow label="Record consent" onAdd={() => setIsAdding(true)} />
      <div className="cb-panel">
        {records === null ? <QuietLine icon="clock">Loading consent</QuietLine> : null}
        {records !== null && records.length === 0 ? <QuietLine icon="check">No consent recorded yet.</QuietLine> : null}
        <ul className="cb-list-rows">
          {(records || []).map((oneRecord) => {
            const saidYes = oneRecord.status === 'granted';

            return (
              <li key={oneRecord.id} className="cb-item">
                <div className="cb-item-top">
                  <span className="cb-item-name">{CONSENT_WORDS[oneRecord.consentType] || oneRecord.consentType}</span>
                  <StatusChip tone={saidYes ? 'ok' : 'neutral'} icon={saidYes ? 'check' : 'close'} word={saidYes ? 'Said yes' : 'Took it back'} />
                </div>
                <div className="cb-item-meta">{[formatMoment(oneRecord.occurredAt), oneRecord.basis].filter(Boolean).join(', ')}</div>
              </li>
            );
          })}
        </ul>
      </div>
      {isAdding ? (
        <SmallFormSheet
          title="Record consent"
          boxes={CONSENT_BOXES}
          startValues={{ consentType: 'marketing_email', status: 'granted' }}
          saveLabel="Record it"
          onSave={saveConsent}
          onClose={() => setIsAdding(false)}
        />
      ) : null}
    </>
  );
}
